import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import WeightedAverageInterface from '@app/Interface/WeightedAverageInterface';
import QualityComponentInterface from '@app/Interface/QualityComponentInterface';
import WeightedAverageHelper from '@app/Service/WeightedAverageHelper';
import QualityComponentHelper from '@app/Service/QualityComponentHelper';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import TabulatorReferenceInterface from '@app/Interface/TabulatorReferenceInterface';
import { Title, Flex, FlexItem, Button, Card, CardBody, CardTitle } from '@patternfly/react-core';
import { DownloadIcon } from '@patternfly/react-icons';
import ItemLoader from '@app/Components/ItemLoader';
import TabulatorFormatters from '@app/utils/TabulatorFormatters';
import moment from 'moment-timezone';
import { useMediaQuery } from '@react-hook/media-query';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';
import TabulatorAccessors from '@app/utils/TabulatorAccessors';

interface DailyComponentsReportProps {
    pageId: number,
    setLastUpdatedText: React.Dispatch<React.SetStateAction<string>>;
    reportType: string;
}

const DailyComponentsReport: React.FC<DailyComponentsReportProps> = ({ pageId, setLastUpdatedText, reportType }) => {
    // In order to get tabulator to work with moment-timezone
    // we need to add moment to the global namespace.
    // see: https://stackoverflow.com/a/54135475/8038146
    window.moment = moment;

    const isSmallScreen = useMediaQuery('only screen and (max-width: 800px)');

    const { userState } = React.useContext(UserContext);

    const [weightedTableRef, setWeightedTableRef] = React.useState<React.MutableRefObject<null>|null>(null);
    const [weightedReportData, setWeightedReportData] = React.useState<WeightedAverageInterface[]>([]);
    const [weightedLoading, setWeightedLoading] = React.useState(true);
    
    const [componentsTableRef, setComponentsTableRef] = React.useState<React.MutableRefObject<null>|null>(null);
    const [componentsReportData, setComponentsReportData] = React.useState<QualityComponentInterface[]>([]);
    const [componentsLoading, setComponentsLoading] = React.useState(true);

    const weightedRefCallback = React.useCallback(node => {
        setWeightedTableRef({current: node});
    }, []);

    const componentsRefCallback = React.useCallback(node => {
        setComponentsTableRef({current: node});
    }, []);

    const formatTime = (time: number) => {
        const timeString = time.toString().padStart(4, '0'); // Ensure the time string is at least 4 characters long
        const hours = timeString.slice(0, -2);
        const minutes = timeString.slice(-2);
        return `${hours}:${minutes}`;
    };

    const weightedTabulatorColumns = [
        {
            title: 'Date', field: 'MonthYear_PK', formatter: "datetime", formatterParams: {
                outputFormat: 'MMMM YYYY'
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'LBS', field: 'Weight', formatter: 'money', formatterParams: {
                precision: false
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Fat', field: 'Fat', formatter: 'money', formatterParams: {
                precision: 2
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Pro', field: 'Protein', formatter: 'money', formatterParams: {
                precision: 2
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'OSL', field: 'OtherSolids', formatter: 'money', formatterParams: {
                precision: 2
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'SNF', field: 'SNF', formatter: 'money', formatterParams: {
                precision: 2
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'SCC', field: 'Leuc', formatter: 'money', formatterParams: {
                precision: false
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'M.U.N.', field: 'MUN', formatter: 'money', formatterParams: {
                precision: 2
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
    ];
    const componentsTabulatorColumns = [
        {
            title: 'Date', field: 'PICKUP_DATE', formatter: "datetime", formatterParams: {
                outputFormat: 'MMM D'
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Time', field: 'PICKUP_TIME', formatter: (cell) => formatTime(cell.getValue())
        },
        { title: 'Tank', field: 'TANK_NUMBER', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        {
            title: 'Lbs', field: 'POUNDS', formatter: 'money', formatterParams: {
                precision: false
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        { title: 'Fat%', field: 'BFAT_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: { flagField: 'BFAT_FLAG', decimalPlaces: 2 }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Pro%', field: 'PROT_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: { flagField: 'PROT_FLAG', decimalPlaces: 2 }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'OSL%', field: 'OSOL_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: { flagField: 'OSOL_FLAG', decimalPlaces: 2 }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'SNF%', field: 'SNF_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: { flagField: 'SNF_FLAG', decimalPlaces: 2 }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'SCC', field: 'LEUC_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: { flagField: 'LEUC_FLAG' }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'M.U.N.', field: 'MUN_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: { flagField: 'MUN_FLAG', decimalPlaces: 2 }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Temp', field: 'TEMPERATURE', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: { flagField: 'TEMPERATURE_FLAG' }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'LPC', field: 'LPC_TEST', visible: true, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
    ];

    // Set last updated text
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        ReportDataHelper
            .getTableUpdatedText(controller, 'ndaWeb_QualityComponents')
            .then(text => { if (mounted) setLastUpdatedText(text) });

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // When the page, user, or FPU change, fetch new report data
    React.useEffect(() => {
        const controller = new AbortController();
        let mounted = true;
        setWeightedLoading(true);
        setComponentsLoading(true);
        if (userState.user?.currentFpuId > 0 && pageId) {
            // Get weighted data
            WeightedAverageHelper.getAverages(controller, userState.user.currentFpuId, pageId)
                .then(weightedRows => { if (mounted) setWeightedReportData(weightedRows) })
                .catch(err => { if (mounted) setWeightedReportData([]) })
                .finally(() => { if (mounted) setWeightedLoading(false) });
            // Get components data
            QualityComponentHelper.getQualityComponents(controller, userState.user.currentFpuId, pageId, 'dailyComponents')
                .then(componentsRows => { if (mounted) setComponentsReportData(componentsRows) })
                .catch(err => { if (mounted) setComponentsReportData([]) })
                .finally(() => { if (mounted) setComponentsLoading(false) });

        } else {
            if (mounted) {
                setWeightedReportData([]);
                setComponentsReportData([]);
                setWeightedLoading(false);
                setComponentsLoading(false);
            }

        }
        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [pageId, userState.user.id, userState.user.currentFpuId]);

    const downloadReportCSV = (isWeighted = false) => {

        const ref = isWeighted ? weightedTableRef : componentsTableRef;

        if (ref?.current) {
            const tabulator: TabulatorReferenceInterface = ref.current;
            const filename = `${reportType}-${isWeighted ? 'weighted-averages' : 'components'}-export.csv`;
            tabulator.table.download('csv', filename);
        } else {
            console.log('Unable to download report CSV');
        }
    };

    const getTable = (isWeighted = false) => {
        const data = isWeighted ? weightedReportData : componentsReportData;
        const columns = isWeighted ? weightedTabulatorColumns : componentsTabulatorColumns;

        const ref = isWeighted ? weightedRefCallback : componentsRefCallback;

        const pdfLink = ReportDataHelper.getReportPdfUrl(
            isWeighted ? 'weighted-monthly-average' : 'daily-components',
            userState.user.currentFpuId
        );
        if (!isWeighted) {
            // check if we need to hide LPC column
            const hasLpcData = QualityComponentHelper.hasLpcData(componentsReportData);

            if (hasLpcData === false) {

                // hide LPC columns
                const lpcIndex = componentsTabulatorColumns.findIndex(element => element.field === "LPC_TEST");

                const lpcColumn = componentsTabulatorColumns[lpcIndex];

                lpcColumn.visible = false;

            }
            
        }
        return (
            <>
                {
                    data.length === 0
                        ? <p><em>No report data to show for the selected producer</em></p>
                        : <>
                            <Flex>
                                <FlexItem align={{ default: 'alignRight' }}>
                                    <Button className="pf-u-float-right" icon={<DownloadIcon />} onClick={e => downloadReportCSV(isWeighted)}>
                                        Download CSV
                                    </Button>
                                    <Button className="pf-u-float-right" icon={<DownloadIcon />} component="a" target="_blank" href={pdfLink}>
                                        Download PDF
                                    </Button>
                                </FlexItem>
                            </Flex>
                            <SCTabulator
                                tableRef={ref}
                                data={data}
                                columns={columns}
                                layout={isSmallScreen ? 'fitData' : 'fitColumns'}
                                options={{
                                    ...options(`nda-report-daily-${isWeighted ? 'weighted' : 'components'}-sc`, true, isWeighted === false ? 'local' : false)
                                }}
                            />
                        </>
                }
            </>
        );
    };

    return (
        <>
            <Card className="pf-u-mb-xl">
                <CardTitle>
                    <Title headingLevel="h4">Weighted Monthly Averages</Title>
                </CardTitle>
                <CardBody>
                    {
                        weightedLoading
                            ? <ItemLoader />
                            : getTable(true)
                    }
                </CardBody>
            </Card>

            <Card className="pf-u-mb-xl">
                <CardTitle>
                    <Title headingLevel="h4">Components</Title>
                </CardTitle>
                <CardBody>
                    {
                        componentsLoading
                            ? <ItemLoader />
                            : getTable(false)
                    }
                </CardBody>
            </Card>
        </>
    );
};

export default DailyComponentsReport;